import React, { useEffect, useState, useCallback } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { fetchPrivilegeChanges } from "../api/client";
import { PrivilegeChange } from "../types";
import DataTable from "../components/DataTable";
import DateRangePicker from "../components/DateRangePicker";
import ExportButton from "../components/ExportButton";
import StatusBadge from "../components/StatusBadge";
import SeverityBadge from "../components/SeverityBadge";
import DetailModal from "../components/DetailModal";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime, utcDaysAgo, utcToday } from "../utils/datetime";

function today(): string {
  return utcToday();
}

type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

function activitySeverity(activityType: string): SeverityLevel {
  const t = activityType.toLowerCase();
  if (t.includes("admin") || t.includes("role")) return "critical";
  if (t.includes("app") || t.includes("application") || t.includes("service principal")) return "high";
  if (t.includes("group") || t.includes("member")) return "medium";
  return "low";
}

function parseDetails(raw?: string): Record<string, string> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null) return parsed as Record<string, string>;
    return { raw: String(parsed) };
  } catch {
    return { raw };
  }
}

export default function PrivilegeChangesPage() {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [changes, setChanges] = useState<PrivilegeChange[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // SOX review default: last 3 months to today
  const [dateFrom, setDateFrom] = useState(utcDaysAgo(90));
  const [dateTo, setDateTo] = useState(today());
  const [initiatedByFilter, setInitiatedByFilter] = useState("");
  const [resultFilter, setResultFilter] = useState("");
  const [modalItem, setModalItem] = useState<PrivilegeChange | null>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string> = { limit: "1000" };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (initiatedByFilter) params.initiated_by = initiatedByFilter;
    if (resultFilter) params.result = resultFilter;

    fetchPrivilegeChanges(params)
      .then((data) => setChanges(data))
      .catch((err) => setError(err.message ?? "Failed to load privilege changes."))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, initiatedByFilter, resultFilter]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: ColumnDef<PrivilegeChange, string>[] = [
    {
      accessorKey: "eventTimestamp",
      header: "Date / Time",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      id: "activity",
      header: "Activity",
      accessorFn: (row) => row.activityDisplayName,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span>{row.original.activityDisplayName}</span>
          <SeverityBadge level={activitySeverity(row.original.activityType)} />
        </div>
      ),
    },
    {
      id: "initiatedBy",
      header: "Initiated By",
      accessorFn: (row) => row.initiatedByUpn || row.initiatedByUser || row.initiatedByApp,
      cell: ({ getValue }) => getValue() || "—",
    },
    { accessorKey: "targetName", header: "Target", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "targetType", header: "Target Type", cell: ({ getValue }) => getValue() || "—" },
    {
      accessorKey: "result",
      header: "Result",
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    },
    {
      id: "expand",
      header: "",
      cell: ({ row }) => (
        <button
          onClick={(e) => { e.stopPropagation(); setModalItem(row.original); }}
          className="text-xs text-blue-600 hover:underline whitespace-nowrap"
        >
          Details
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Privilege Changes</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Azure AD role and permission change audit trail.
          </p>
        </div>
        <ExportButton
          endpoint="/api/privilege-changes/export"
          params={{
            date_from: dateFrom,
            date_to: dateTo,
            initiated_by: initiatedByFilter || undefined,
            result: resultFilter || undefined,
          }}
          filename="privilege-changes-export.csv"
        />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-4">
          <DateRangePicker
            label="Date range:"
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Initiated By</label>
            <input
              type="text"
              value={initiatedByFilter}
              onChange={(e) => setInitiatedByFilter(e.target.value)}
              placeholder="user@example.com"
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-52"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Result</label>
            <select
              value={resultFilter}
              onChange={(e) => setResultFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-36"
            >
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="failure">Failure</option>
            </select>
          </div>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 transition-colors self-end"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading privilege changes…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchData} className="ml-4 text-red-600 underline text-xs">Retry</button>
        </div>
      )}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-500">{changes.length} record{changes.length !== 1 ? "s" : ""}</p>
          <DataTable data={changes} columns={columns} initialPageSize={50} />
        </>
      )}

      {modalItem && (() => {
        const details = parseDetails(modalItem.additionalDetails);
        const initiator = modalItem.initiatedByUpn || modalItem.initiatedByUser || modalItem.initiatedByApp || "—";
        return (
          <DetailModal
            title={modalItem.activityDisplayName}
            subtitle={`${formatDate(modalItem.eventTimestamp)} · ${initiator}`}
            onClose={() => setModalItem(null)}
          >
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-5">
              {[
                { label: "Activity Type", value: modalItem.activityType },
                { label: "Result", value: modalItem.result },
                { label: "Initiated By", value: initiator },
                { label: "Target", value: modalItem.targetName },
                { label: "Target Type", value: modalItem.targetType },
                { label: "Target Resource ID", value: modalItem.targetResourceId },
              ].map(({ label, value }) => (
                <div key={label}>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
                  <dd className="mt-0.5 text-sm text-gray-900 break-all">{value || "—"}</dd>
                </div>
              ))}
            </dl>
            {details && Object.keys(details).length > 0 && (
              <>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Additional Details</h3>
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide w-1/3">Key</th>
                        <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {Object.entries(details).map(([k, v]) => (
                        <tr key={k} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium text-gray-600 align-top">{k}</td>
                          <td className="px-4 py-2 text-gray-700 break-all align-top">{String(v)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {(!details || Object.keys(details).length === 0) && (
              <p className="text-sm text-gray-400">No additional details.</p>
            )}
          </DetailModal>
        );
      })()}
    </div>
  );
}
